import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import {
	ChevronDownIcon,
	ChevronRightIcon,
	MagnifyingGlassIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import api from "../../../../utils/axios";

export default function ProductsModal({ show, lab, closeProductModal }) {
	const [activeTab, setActiveTab] = useState("products");
	const [searchTerm, setSearchTerm] = useState("");
	const [openGroupIds, setOpenGroupIds] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [labDetail, setLabDetail] = useState(null);

	useEffect(() => {
		if (!show) return;

		const getModalData = async () => {
			try {
				setIsLoading(true);

		const labResponse = await api.get(`/laboratory/${lab._id}/products`);

		setLabDetail({
		...lab,
		...labResponse.data,
		});
			} catch (error) {
				console.log("Error al cargar datos del modal:", error);
			} finally {
				setIsLoading(false);
			}
		};

		setActiveTab("products");
		setSearchTerm("");
		setOpenGroupIds([]);
		setLabDetail(null);
		getModalData();
	}, [show, lab?._id]);

	const getId = (item) => {
		return item?._id || item?.id || item;
	};

	const sameId = (firstId, secondId) => {
		return String(firstId) === String(secondId);
	};

	const getProductKey = (product, index) => {
		return product?._id || product?.id || `${product?.name || "product"}-${index}`;
	};

	const getProductCode = (product) => {
		return product?.code || product?.codigo || product?.productCode || product?.sku || "";
	};

	const getProductName = (product) => {
		return product?.name || product?.description || product?.label || "";
	};

	const isFullProduct = (product) => {
		return product && typeof product === "object" && getProductName(product);
	};

	const getCategoryId = (product) => {
		if (!product?.category) return "without-category";

		if (typeof product.category === "string") {
			return product.category;
		}

		return product.category._id || product.category.id || "without-category";
	};

	const getCategoryName = (categoryId, product) => {
		if (product?.category && typeof product.category === "object" && product.category.name) {
			return product.category.name;
		}

		return "Sin categoría";
	};

	const modalLab = labDetail || lab;

	const labTags = modalLab?.tags || [];
	const labProducts = modalLab?.products || [];
	const labOtherProducts = modalLab?.otherProducts || [];

	const fullAssignedTags = useMemo(() => {
		return labTags.filter((tag) => tag && typeof tag === "object");
	}, [labTags]);

	const assignedBioTags = useMemo(() => {
		return fullAssignedTags.filter((tag) => tag.isProducts);
	}, [fullAssignedTags]);

	const assignedOtherTags = useMemo(() => {
		return fullAssignedTags.filter((tag) => !tag.isProducts);
	}, [fullAssignedTags]);

	const rawBioProducts = useMemo(() => {
		return labProducts.filter((product) => {
			const isTag =
				product?.isProducts !== undefined ||
				Array.isArray(product?.products) ||
				Array.isArray(product?.otherProducts);

			return !isTag && isFullProduct(product);
		});
	}, [labProducts]);

	const rawOtherProducts = useMemo(() => {
		return labOtherProducts.filter((product) => {
			const isTag =
				product?.isProducts !== undefined ||
				Array.isArray(product?.products) ||
				Array.isArray(product?.otherProducts);

			return !isTag && isFullProduct(product);
		});
	}, [labOtherProducts]);

	// Los labs nuevos pueden guardar bioquímicos como productos sueltos.
	// Buscamos su tag original para mostrarlos agrupados en el modal.
	const findBioTagForProduct = (product) => {
		return assignedBioTags.find((tag) =>
			(tag.products || []).some((tagProduct) =>
			sameId(getId(tagProduct), getId(product))
			)
		);
	};

	const bioGroups = useMemo(() => {
		const groupsByTag = new Map();

		assignedBioTags.forEach((tag) => {
			const products = (tag.products || []).filter(isFullProduct);

			if (products.length === 0) return;

			groupsByTag.set(String(tag._id), {
			id: `tag-${tag._id}`,
			name: tag.name || "Etiqueta sin nombre",
			type: "Etiqueta",
			products,
			});
		});

		const productsWithoutRealTag = [];

		// Reubica productos sueltos bajo su tag real; solo quedan en "Sin etiqueta"
		// cuando no pertenecen a ningún tag.
		rawBioProducts.forEach((product) => {
			const productTag = findBioTagForProduct(product);

			if (!productTag) {
			productsWithoutRealTag.push(product);
			return;
			}

			const tagId = String(productTag._id);
			const existingGroup = groupsByTag.get(tagId);

			if (existingGroup) {
			if (
				!existingGroup.products.some((groupProduct) =>
				sameId(getId(groupProduct), getId(product))
				)
			) {
				existingGroup.products.push(product);
			}

			return;
			}

			groupsByTag.set(tagId, {
			id: `tag-${productTag._id}`,
			name: productTag.name || "Etiqueta sin nombre",
			type: "Etiqueta",
			products: [product],
			});
		});

		const tagGroups = Array.from(groupsByTag.values()).filter(
			(group) => group.products.length > 0
		);

		// Fallback para productos que realmente no tienen etiqueta.
		if (productsWithoutRealTag.length > 0) {
			tagGroups.push({
			id: "bio-without-tag",
			name: "Sin etiqueta",
			type: "Productos sin etiqueta",
			products: productsWithoutRealTag,
			});
		}

		return tagGroups;
	}, [assignedBioTags, rawBioProducts]);

	const variousGroups = useMemo(() => {
		const productsFromTags = assignedOtherTags.flatMap((tag) =>
			(tag.otherProducts || tag.products || []).filter(isFullProduct)
		);

		const allVariousProducts = [...rawOtherProducts, ...productsFromTags];

		const uniqueProductsById = new Map();

		allVariousProducts.forEach((product) => {
			const productId = getId(product);

			if (productId) {
			uniqueProductsById.set(String(productId), product);
			}
		});

		const groupsByCategory = Array.from(uniqueProductsById.values()).reduce(
			(groups, product) => {
			const categoryId = getCategoryId(product);

			if (!groups[categoryId]) {
				groups[categoryId] = {
				id: `category-${categoryId}`,
				name: getCategoryName(categoryId, product),
				type:
					categoryId === "without-category"
					? "Productos sin categoría"
					: "Categoría",
				products: [],
				};
			}

			groups[categoryId].products.push(product);
			return groups;
			},
			{}
		);

		return Object.values(groupsByCategory).filter(
			(group) => group.products.length > 0
		);
	}, [assignedOtherTags, rawOtherProducts]);

	const currentGroups = activeTab === "products" ? bioGroups : variousGroups;

	const productMatchesSearch = (product, normalizedSearchTerm) => {
		const productName = getProductName(product);
		const productCode = getProductCode(product);

		return `${productName} ${productCode}`.toLowerCase().includes(normalizedSearchTerm);
	};

	const filteredGroups = useMemo(() => {
		const normalizedSearchTerm = searchTerm.trim().toLowerCase();

		if (!normalizedSearchTerm) {
			return currentGroups;
		}

		return currentGroups
			.map((group) => {
				const groupNameMatches = group.name.toLowerCase().includes(normalizedSearchTerm);

				if (groupNameMatches) {
					return group;
				}

				const filteredProducts = group.products.filter((product) =>
					productMatchesSearch(product, normalizedSearchTerm)
				);

				return {
					...group,
					products: filteredProducts,
				};
			})
			.filter((group) => group.products.length > 0);
	}, [currentGroups, searchTerm]);

	const bioProductsCount = bioGroups.reduce((total, group) => total + group.products.length, 0);
	const variousProductsCount = variousGroups.reduce((total, group) => total + group.products.length, 0);

	const toggleGroup = (groupId) => {
		setOpenGroupIds((prevOpenGroupIds) => {
			if (prevOpenGroupIds.includes(groupId)) {
				return prevOpenGroupIds.filter((id) => id !== groupId);
			}

			return [...prevOpenGroupIds, groupId];
		});
	};

	return (
		<Modal
			show={show}
			onHide={closeProductModal}
			centered
			contentClassName="tw-overflow-hidden tw-rounded-lg tw-border-0"
		>
			<div className="tw-bg-white tw-p-5">
				<div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
					<div>
						<h2 className="tw-m-0 tw-text-lg tw-font-bold tw-text-gray-950">
							Productos{modalLab?.name ? ` - ${modalLab.name}` : ""}
						</h2>

						{modalLab ? (
							<p>
								Sede {modalLab.officeNumber || "-"} • {modalLab.address || "-"}
							</p>
						) : null}
					</div>

					<button
						type="button"
						onClick={closeProductModal}
						className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-gray-500 tw-transition hover:tw-bg-gray-100 hover:tw-text-gray-900"
						aria-label="Cerrar modal"
					>
						<XMarkIcon className="tw-h-5 tw-w-5" />
					</button>
				</div>

				<div className="tw-relative tw-mt-4">
					<MagnifyingGlassIcon className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw-h-5 tw-w-5 -tw-translate-y-1/2 tw-text-gray-400" />
					<input
						type="text"
						value={searchTerm}
						onChange={(event) => setSearchTerm(event.target.value)}
						placeholder="Buscar etiquetas, categorías o productos..."
						className="tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-py-2 tw-pl-10 tw-pr-3 tw-text-sm tw-text-gray-900 tw-outline-none tw-transition focus:tw-border-gray-500 focus:tw-ring-2 focus:tw-ring-gray-200"
					/>
				</div>

				<div className="tw-mt-3 tw-grid tw-grid-cols-2 tw-rounded-xl tw-bg-gray-100 tw-p-1">
					<button
						type="button"
						onClick={() => {
							setActiveTab("products");
							setOpenGroupIds([]);
						}}
						style={
							activeTab === "products"
								? { backgroundColor: "var(--secondary-container)" }
								: undefined
						}
						className={`tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-transition ${
							activeTab === "products"
								? "tw-text-gray-950 tw-shadow-sm"
								: "tw-text-gray-600 hover:tw-text-gray-950"
						}`}
					>
						Bioquímicos ({bioProductsCount})
					</button>

					<button
						type="button"
						onClick={() => {
							setActiveTab("otherProducts");
							setOpenGroupIds([]);
						}}
						style={
							activeTab === "otherProducts"
								? { backgroundColor: "var(--secondary-container)" }
								: undefined
						}
						className={`tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-transition ${
							activeTab === "otherProducts"
								? "tw-text-gray-950 tw-shadow-sm"
								: "tw-text-gray-600 hover:tw-text-gray-950"
						}`}
					>
						Varios ({variousProductsCount})
					</button>
				</div>

				<div className="tw-mt-4 tw-max-h-[320px] tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-200">
					{isLoading ? (
						<div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-white tw-px-3 tw-py-8 tw-text-sm tw-text-gray-500">
							<Spinner animation="border" size="sm" />
							Cargando productos...
						</div>
					) : filteredGroups.length > 0 ? (
						filteredGroups.map((group) => {
							const isOpen = openGroupIds.includes(group.id);

							return (
								<div key={group.id} className="tw-border-b tw-border-gray-200 last:tw-border-b-0">
									<button
										type="button"
										onClick={() => toggleGroup(group.id)}
										className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-bg-white tw-px-3 tw-py-3 tw-text-left tw-text-sm tw-transition hover:tw-bg-gray-50"
									>
										<div className="tw-flex tw-items-center tw-gap-2">
											{isOpen ? (
												<ChevronDownIcon className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-gray-400" />
											) : (
												<ChevronRightIcon className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-gray-400" />
											)}

											<p className="tw-m-0 tw-text-sm tw-font-medium tw-text-gray-900">
												<span>{group.name}</span>{" "}
												<span className="tw-font-medium tw-text-gray-500">
													({group.products.length})
												</span>
											</p>
										</div>

										<span className="tw-inline-flex tw-h-8 tw-min-w-8 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-200 tw-bg-gray-50 tw-px-2 tw-text-xs tw-font-medium tw-text-gray-600">
											{group.products.length}
										</span>
									</button>

									{isOpen ? (
										<div className="tw-bg-gray-50">
											{group.products.map((product, index) => {
												const productCode = getProductCode(product);

												return (
													<div
														key={getProductKey(product, index)}
														className="tw-border-t tw-border-gray-200 tw-bg-white tw-px-9 tw-py-3"
													>
														<p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-gray-950">
															{getProductName(product)}
														</p>

														{productCode ? (
															<p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-gray-500">
															Código: {productCode}
															</p>
														) : null}
													</div>
												);
											})}
										</div>
									) : null}
								</div>
							);
						})
					) : (
						<div className="tw-bg-white tw-px-3 tw-py-6 tw-text-center tw-text-sm tw-text-gray-500">
							No se encontraron etiquetas o categorías con productos
						</div>
					)}
				</div>
			</div>
		</Modal>
	);
}
